import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Attraction } from "@/types/attraction";
import {
  Mountain,
  Camera,
  Train,
  Utensils,
  Clock,
  MapPin,
  ExternalLink,
  X,
} from "lucide-react";

interface AttractionModalProps {
  attraction: Attraction;
  children: React.ReactNode;
}

const AttractionModal = ({ attraction, children }: AttractionModalProps) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "nature":
        return Mountain;
      case "culture":
        return Camera;
      case "activity":
        return Train;
      case "dining":
        return Utensils;
      default:
        return Mountain;
    }
  };

  const IconComponent = getCategoryIcon(attraction.category);

  return (
    <Dialog data-oid="tqperdw">
      <DialogTrigger asChild data-oid="rvfs1jr">
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0" data-oid="7h1c7ej">
        <ScrollArea className="max-h-[90vh]" data-oid="eh9uoca">
          <div className="relative" data-oid="w_32p6_">
            {/* Header Image */}
            <div className="relative h-64 overflow-hidden" data-oid="k8s2rd:">
              <img
                src={attraction.image}
                alt={attraction.name}
                className="w-full h-full object-cover"
                data-oid="_30u-lt"
              />

              <div
                className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
                data-oid="5yby-bm"
              />

              <div
                className="absolute top-4 left-4 flex gap-2"
                data-oid="a4vockt"
              >
                <Badge
                  className="bg-[#A38648] hover:bg-[#A38648]/90 text-white"
                  data-oid="c8.jwqc"
                >
                  {attraction.distance}
                </Badge>
                <Badge
                  variant="secondary"
                  className="bg-white/90 text-gray-900 hover:bg-white"
                  data-oid="jb108cv"
                >
                  <IconComponent className="w-3 h-3 mr-1" data-oid="vt48b27" />
                  {attraction.category.charAt(0).toUpperCase() +
                    attraction.category.slice(1)}
                </Badge>
              </div>
              <DialogHeader
                className="absolute bottom-4 left-4 right-4"
                data-oid=".cqg.lz"
              >
                <DialogTitle
                  className="text-2xl text-white font-playfair"
                  data-oid="o_fqvfv"
                >
                  {attraction.name}
                </DialogTitle>
                <div
                  className="flex items-center gap-4 text-white/90 text-sm"
                  data-oid="7o62y3b"
                >
                  <div className="flex items-center gap-1" data-oid="y.tngj7">
                    <Clock className="w-4 h-4" data-oid="e283e:z" />
                    <span data-oid="y8hewsr">{attraction.duration}</span>
                  </div>
                  <div className="flex items-center gap-1" data-oid="xd30w5m">
                    <MapPin className="w-4 h-4" data-oid="evt-.dg" />
                    <span data-oid="qra0eg3">{attraction.location}</span>
                  </div>
                </div>
              </DialogHeader>
            </div>

            {/* Content */}
            <div className="p-6" data-oid="5qp75mf">
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
                data-oid="_lym_e6"
              >
                {/* Left Column */}
                <div data-oid="voev-yp">
                  <h3
                    className="text-xl text-gray-900 mb-4 font-playfair"
                    data-oid="4o48j3e"
                  >
                    About {attraction.name}
                  </h3>
                  <p
                    className="text-gray-700 mb-6 leading-relaxed"
                    data-oid="55pxoap"
                  >
                    {attraction.description}
                  </p>

                  <div className="space-y-4" data-oid="qck22at">
                    <div data-oid="2l4o3rf">
                      <h4
                        className="font-medium text-gray-900 text-sm mb-2"
                        data-oid="a5f:-:_"
                      >
                        Features:
                      </h4>
                      <div className="flex flex-wrap gap-2" data-oid="xp53pl-">
                        {attraction.features.map((feature, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="text-xs"
                            data-oid="2uat2_a"
                          >
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {attraction.openingHours && (
                      <div data-oid="isu_ii5">
                        <h4
                          className="font-medium text-gray-900 text-sm mb-2"
                          data-oid="8znfd4u"
                        >
                          Opening Hours:
                        </h4>
                        <p className="text-sm text-gray-600" data-oid="wwfr724">
                          {attraction.openingHours}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column */}
                <div data-oid="y77lnd7">
                  <h3
                    className="text-xl text-gray-900 mb-4 font-playfair"
                    data-oid="yhkin6q"
                  >
                    Guest Benefits
                  </h3>
                  <ul className="space-y-3 mb-6" data-oid="-da5f55">
                    {attraction.guestBenefits.map((benefit, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3"
                        data-oid="mtg7j3d"
                      >
                        <div
                          className="w-2 h-2 bg-[#A38648] rounded-full mt-2 flex-shrink-0"
                          data-oid="x2cfgpv"
                        />

                        <span
                          className="text-gray-700 text-sm leading-relaxed"
                          data-oid="w:g2jw9"
                        >
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Additional Images */}
                  <div
                    className="grid grid-cols-2 gap-3 mb-6"
                    data-oid="vpmht2n"
                  >
                    {attraction.images.slice(1, 3).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${attraction.name} ${index + 2}`}
                        className="w-full h-24 object-cover rounded-lg"
                        data-oid="osdnd18"
                      />
                    ))}
                  </div>

                  {/* Website Link */}
                  {attraction.website && (
                    <div
                      className="pt-4 border-t border-gray-200"
                      data-oid="f2tkvgz"
                    >
                      <a
                        href={attraction.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#A38648] hover:text-[#D62429] font-medium transition-colors"
                        data-oid="jwkzbef"
                      >
                        <ExternalLink className="w-4 h-4" data-oid="pk_vjno" />
                        Visit Official Website
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div
                className="mt-8 pt-6 border-t border-gray-200"
                data-oid="shapo5-"
              >
                <div
                  className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center"
                  data-oid="p0jkuiq"
                >
                  <div data-oid="heebvxj">
                    <div className="text-2xl text-[#A38648]" data-oid="4ww6g0q">
                      {attraction.distance}
                    </div>
                    <div className="text-xs text-gray-600" data-oid="ipcv.3l">
                      Distance
                    </div>
                  </div>
                  <div data-oid="sih13y0">
                    <div className="text-2xl text-[#A38648]" data-oid="8oktb9s">
                      {attraction.duration}
                    </div>
                    <div className="text-xs text-gray-600" data-oid="u_x_w8:">
                      Duration
                    </div>
                  </div>
                  <div data-oid="f._vagz">
                    <div
                      className="text-lg text-[#A38648] capitalize"
                      data-oid="02527w9"
                    >
                      {attraction.difficulty}
                    </div>
                    <div className="text-xs text-gray-600" data-oid="f3_61tz">
                      Difficulty
                    </div>
                  </div>
                  <div data-oid="ia3yrhj">
                    <div
                      className="text-lg text-[#A38648] capitalize"
                      data-oid="sku-et9"
                    >
                      {attraction.priceRange}
                    </div>
                    <div className="text-xs text-gray-600" data-oid="0yi3n3j">
                      Price Range
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AttractionModal;
